const FALLBACK_FILE_NAME = 'download'

/**
 * Last path segment of a URL, ignoring the query. Presigned S3 keys keep the
 * original extension (`a1b2c3d4-1755000000.jpg`), so that segment is the only
 * name the client has for log media.
 */
export const fileNameFromUrl = (url: string): string => {
  try {
    const segment = new URL(url, window.location.origin).pathname
      .split('/')
      .pop()

    return segment ? decodeURIComponent(segment) : FALLBACK_FILE_NAME
  } catch {
    return FALLBACK_FILE_NAME
  }
}

/**
 * Saves a remote file to disk.
 *
 * An `<a download>` pointing straight at the file does not work for our media:
 * browsers ignore `download` on a cross-origin href, and log media is served
 * from S3, so the click degrades to a plain navigation. What that navigation
 * then does is decided by the object's stored content type — photos uploaded
 * through the older multipart route carry `binary/octet-stream` and download,
 * while ones uploaded through the presigned route carry `image/jpeg` and render
 * inline in a new tab. Fetching the bytes first and anchoring at a same-origin
 * blob URL makes `download` authoritative again for both.
 *
 * `cache: 'reload'` is required because the page has usually already loaded the
 * same URL in a plain `<img>`: that load sends no `Origin`, so S3 skips CORS
 * evaluation and the cached response carries no `Access-Control-Allow-Origin`.
 * Reading it again through `fetch` is then blocked by the cached response
 * rather than by S3 (the same trap CM-2854 hit in the chart PDF export).
 */
export const downloadUrl = async (
  url: string,
  fileName?: string
): Promise<void> => {
  const response = await fetch(url, { cache: 'reload' })
  if (!response.ok) {
    throw new Error(`Download request failed with status ${response.status}`)
  }

  const blobUrl = window.URL.createObjectURL(await response.blob())
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = fileName || fileNameFromUrl(url)

  document.body.appendChild(link)
  link.click()

  setTimeout(() => {
    document.body.removeChild(link)
    window.URL.revokeObjectURL(blobUrl)
  }, 100)
}
